import { useState, type CSSProperties } from 'react';
import { clsx } from 'clsx';
import { StatusBadge } from '@/shared/ui';
import type { SectionScore } from '../api/seoApi';
import styles from './SectionBar.module.css';

function scoreColor(score: number): string {
  if (score >= 70) return 'var(--success)';
  if (score >= 55) return 'var(--warning)';
  if (score >= 40) return 'var(--warning-hover)';
  return 'var(--error)';
}

interface Props {
  name: string;
  weight: number;
  score: number;
  passed: number;
  total: number;
  checks: SectionScore['checks'];
}

export function SectionBar({ name, weight, score, passed, total, checks }: Props) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ '--bar-color': scoreColor(score) } as CSSProperties}>
      <div
        onClick={() => setExpanded(!expanded)}
        className={clsx(styles.header, expanded && styles.headerExpanded)}
      >
        <div className={styles.topRow}>
          <div className={styles.nameGroup}>
            <span className={styles.name}>{name}</span>
            <span className={styles.weightPill}>{Math.round(weight * 100)}%</span>
          </div>
          <div className={styles.scoreGroup}>
            <span className={styles.passCount}>
              {passed}/{total}
            </span>
            <span className={styles.score}>{Math.round(score)}</span>
            <span className={clsx(styles.chevron, expanded && styles.chevronExpanded)}>▼</span>
          </div>
        </div>
        <div className={styles.progressTrack}>
          <div className={styles.progressFill} style={{ width: `${Math.min(score, 100)}%` }} />
        </div>
      </div>

      {expanded && checks.length > 0 && (
        <div className={styles.checksGrid}>
          {checks.map((c) => (
            <div
              key={c.id}
              className={clsx(styles.checkRow, c.status === 'fail' && styles.checkRowFail)}
            >
              <span className={styles.checkLabel}>
                <span className={styles.checkId}>{c.id}</span>
                {c.name}
              </span>
              <div className={styles.checkMeta}>
                {c.note && <span className={styles.checkNote}>{c.note}</span>}
                <StatusBadge status={c.status} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
